@if (loadState() === LoadState.LOADING || loadState() === LoadState.ERROR) {
  <div
    class="skeleton-table flex-col gap-12 w-100"
    [style.--skeleton-columns]="skeletonGridColumns()">
    <div class="skeleton-table-header">
      @for (column of displayedColumns(); track column) {
        <div
          class="skeleton skeleton-cell"
          [class.skeleton-cell--box]="column === 'select' || column === 'actions'"></div>
      }
    </div>
    @for (row of [1, 2, 3, 4, 5]; track row) {
      <div class="skeleton-table-row">
        @for (column of displayedColumns(); track column) {
          @switch (column) {
            @case ('name') {
              <div class="skeleton-col-name-cell flex-row gap-8 items-center">
                <div class="skeleton skeleton-name-bar"></div>
                <div class="skeleton skeleton-erp-bar"></div>
              </div>
            }
            @case ('requester') {
              <div class="skeleton-requester flex-row gap-8 items-center">
                <div class="skeleton skeleton-avatar-circle"></div>
                <div class="skeleton skeleton-cell"></div>
              </div>
            }
            @case ('select') {
              <div class="skeleton skeleton-cell skeleton-cell--box"></div>
            }
            @case ('actions') {
              <div class="skeleton skeleton-cell skeleton-cell--box"></div>
            }
            @case ('costCenter') {
              <div class="skeleton skeleton-cell skeleton-cell--chip"></div>
            }
            @case ('branch') {
              <div class="skeleton skeleton-cell skeleton-cell--chip"></div>
            }
            @case ('suppliers') {
              <div class="skeleton skeleton-cell skeleton-cell--chip"></div>
            }
            @case ('status') {
              <div class="skeleton skeleton-cell skeleton-cell--chip"></div>
            }
            @default {
              <div class="skeleton skeleton-cell"></div>
            }
          }
        }
      </div>
    }
  </div>
} @else if (loadState() === LoadState.LOADED && items().length === 0) {
  <aionz-empty-content
    title="Nenhum item nesta solicitação"
    description="Quando itens forem adicionados, eles aparecem aqui."
    icon="inventory_2"></aionz-empty-content>
} @else {
  <seed-card-table
    tableContent
    [scrollOnOverflow]="true">
    <seed-card-table-body>
      <table
        mat-table
        [dataSource]="dataSource()">
        @if (showSelection()) {
          <ng-container matColumnDef="select">
            <th
              mat-header-cell
              *matHeaderCellDef>
              <mat-checkbox
                (change)="$event ? selectAllChange.emit(isAllSelected()) : null"
                [checked]="isAllSelected()"
                [indeterminate]="isSomeSelected()"
                aria-label="Selecionar todos os itens">
              </mat-checkbox>
            </th>
            <td
              mat-cell
              *matCellDef="let row">
              <mat-checkbox
                (click)="$event.stopPropagation()"
                (change)="$event ? rowSelectChange.emit(row.orderId) : null"
                [checked]="selectedIds().has(row.orderId)"
                [attr.aria-label]="'Selecionar item ' + row.name">
              </mat-checkbox>
            </td>
          </ng-container>
        }

        <ng-container matColumnDef="id">
          <th
            mat-header-cell
            *matHeaderCellDef>
            Código
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            {{ row.productId }}
          </td>
        </ng-container>

        <ng-container matColumnDef="name">
          <th
            mat-header-cell
            *matHeaderCellDef>
            Nome do Produto
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            <!-- Mesmo padrão do mapa de cotação: nome e, na mesma linha, o código ERP sempre visível. -->
            <div class="product-name-cell">
              <span
                class="product-name"
                [aionzTooltip]="row.name"
                mode="message"
                >{{ row.name }}</span
              >
              @if (row.ERPId) {
                <mat-basic-chip class="erp-chip">ERP {{ row.ERPId }}</mat-basic-chip>
              }
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="unit">
          <th
            mat-header-cell
            *matHeaderCellDef>
            Unidade
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            <p
              class="unit-cell text-truncate"
              [aionzTooltip]="row.unit"
              mode="message">
              {{ row.unit }}
            </p>
          </td>
        </ng-container>

        <ng-container matColumnDef="quantity">
          <th
            mat-header-cell
            *matHeaderCellDef>
            Qtd.
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            <div class="quantity-cell flex-row items-center gap-4">
              @if (editingOrderId() === row.orderId) {
                <input
                  type="number"
                  class="quantity-input"
                  min="1"
                  step="any"
                  [attr.aria-label]="'Quantidade de ' + row.name"
                  [formControl]="quantityControl"
                  [class.quantity-input--invalid]="
                    quantityControl.invalid && quantityControl.touched
                  "
                  (keydown.enter)="saveQuantity(row)"
                  (keydown.escape)="cancelEditQuantity()"
                  (blur)="onQuantityBlur($event, row)" />
                <button
                  mat-icon-button
                  type="button"
                  class="quantity-action-btn quantity-save-btn save"
                  [attr.aria-label]="'Salvar quantidade para ' + row.name"
                  [disabled]="!canSaveQuantity(row)"
                  (click)="saveQuantity(row)">
                  <mat-icon>check</mat-icon>
                </button>
              } @else {
                <span class="quantity-text">{{ row.quantity | number }}</span>
                @if (allowEditQuantity()) {
                  <button
                    mat-icon-button
                    type="button"
                    class="quantity-action-btn edit"
                    [attr.aria-label]="'Editar quantidade para ' + row.name"
                    (click)="startEditQuantity(row)">
                    <mat-icon>edit</mat-icon>
                  </button>
                }
              }
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="specification">
          <th
            mat-header-cell
            *matHeaderCellDef>
            Info. Adicionais
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            <p
              class="specification-cell text-truncate"
              [aionzTooltip]="row.specification"
              mode="message">
              {{ row.specification }}
            </p>
          </td>
        </ng-container>

        <ng-container matColumnDef="requester">
          <th
            mat-header-cell
            *matHeaderCellDef>
            Solicitante
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            <div class="requester-cell">
              <seed-user-avatar
                [size]="24"
                [src]="row.requester.avatarUrl" />
              <span
                class="requester-name"
                [aionzTooltip]="row.requester.name"
                mode="message"
                >{{ row.requester.name }}</span
              >
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="costCenter">
          <th
            mat-header-cell
            *matHeaderCellDef>
            CC
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            <mat-basic-chip class="cost-center-chip">
              {{ row.costCenter }}
            </mat-basic-chip>
          </td>
        </ng-container>

        <ng-container matColumnDef="branch">
          <th
            mat-header-cell
            *matHeaderCellDef>
            Local de entrega
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            @let branch = branchLabel(row);
            @if (branch) {
              <!-- Mesmo tooltip do chip de locais do topo: nome, rua, cidade/UF e CEP. -->
              <mat-basic-chip
                class="branch-chip"
                [aionzTooltipComponent]="branchesTooltipComponent"
                [aionzTooltipData]="branchTooltips().get(row.orderId)"
                aionzTooltipPosition="below"
                aionzTooltipClass="branches-tooltip"
                mode="message">
                <span class="branch-chip-text">{{ branch }}</span>
              </mat-basic-chip>
            } @else {
              <span class="branch-fallback">—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="suppliers">
          <th
            mat-header-cell
            *matHeaderCellDef>
            Fornecedores
          </th>
          <td
            mat-cell
            *matCellDef="let row">
            <!-- Botão de verdade (o mat-basic-chip é decorativo: cursor padrão e sem foco). -->
            <button
              type="button"
              class="suppliers-chip"
              [class.suppliers-chip--empty]="row.suppliers.length === 0 && supplierRequired()"
              [class.suppliers-chip--none]="row.suppliers.length === 0 && !supplierRequired()"
              [class.suppliers-chip--static]="!suppliersClickable()"
              [disabled]="!suppliersClickable() && row.suppliers.length === 0"
              matBadge="!"
              [matBadgeHidden]="!hasPendingSuggestedSupplier(row.suppliers)"
              matBadgeColor="warn"
              matBadgeSize="small"
              [attr.aria-label]="
                (row.suppliers.length === 0
                  ? 'Sem fornecedor'
                  : row.suppliers.length + ' ' + ('fornecedor' | pluralizador: row.suppliers.length)) +
                (hasPendingSuggestedSupplier(row.suppliers)
                  ? '. Há fornecedor sugerido pendente de aprovação'
                  : '')
              "
              [aionzTooltipComponent]="row.suppliers.length ? itemSuppliersTooltipComponent : null"
              [aionzTooltipData]="row.suppliers.length ? getSuppliersTooltipData(row) : null"
              [aionzTooltip]="
                row.suppliers.length === 0
                  ? supplierRequired()
                    ? 'Sem fornecedor: adicione ao menos um para enviar para cotação'
                    : 'Os fornecedores são definidos depois que a solicitação for aceita'
                  : null
              "
              aionzTooltipPosition="below"
              aionzTooltipClass="item-suppliers-tooltip"
              mode="message"
              (click)="suppliersClickable() && handleSuppliersClick(row)">
              @if (row.suppliers.length === 0) {
                @if (supplierRequired()) {
                  <mat-icon aria-hidden="true">warning_amber</mat-icon>
                }
                <span>Sem fornecedor</span>
              } @else {
                <mat-icon aria-hidden="true">storefront</mat-icon>
                <span
                  >{{ row.suppliers.length }}
                  {{ 'fornecedor' | pluralizador: row.suppliers.length }}</span
                >
              }
            </button>
          </td>
        </ng-container>

        @if (showStatus()) {
          <ng-container matColumnDef="status">
            <th
              mat-header-cell
              *matHeaderCellDef>
              Status
            </th>
            <td
              mat-cell
              *matCellDef="let row">
              <mat-basic-chip
                class="custom-status-chip"
                [class]="getStatusClass(row.status)">
                <span>{{ row.status || PurchaseItemStatus.PENDING }}</span>
              </mat-basic-chip>
            </td>
          </ng-container>
        }

        @if (showActions() && actions().length > 0) {
          <ng-container matColumnDef="actions">
            <th
              mat-header-cell
              *matHeaderCellDef>
              Ações
            </th>
            <td
              mat-cell
              *matCellDef="let row">
              <div class="row-actions">
                @for (action of getInlineActions(row); track action.type) {
                  <button
                    mat-button
                    type="button"
                    class="row-action-btn"
                    [class.row-action-btn--positive]="action.tone === 'positive'"
                    [class.row-action-btn--negative]="action.tone === 'negative'"
                    [attr.aria-label]="action.label + ': ' + row.name"
                    (click)="handleAction(action, row)">
                    <mat-icon aria-hidden="true">{{ action.icon }}</mat-icon>
                    {{ action.shortLabel || action.label }}
                  </button>
                }
                @if (getMenuActions(row).length > 0) {
                  <button
                    mat-icon-button
                    type="button"
                    [attr.aria-label]="'Mais ações para ' + row.name"
                    [matMenuTriggerFor]="menu">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #menu="matMenu">
                    @for (action of getMenuActions(row); track action.type) {
                      <button
                        mat-menu-item
                        type="button"
                        (click)="handleAction(action, row)">
                        <div class="flex-row items-center gap-8">
                          <mat-icon>{{ action.icon }}</mat-icon>
                          <span>{{ action.label }}</span>
                        </div>
                      </button>
                    }
                  </mat-menu>
                }
              </div>
            </td>
          </ng-container>
        }

        <tr
          mat-header-row
          *matHeaderRowDef="displayedColumns()"></tr>
        <tr
          mat-row
          [class.row--no-supplier]="row.suppliers.length === 0 && supplierRequired()"
          *matRowDef="let row; columns: displayedColumns()"></tr>

        <tr
          class="mat-row"
          *matNoDataRow>
          <td
            class="mat-cell text-center py-3"
            [attr.colspan]="displayedColumns().length">
            Nenhum dado encontrado!
          </td>
        </tr>
      </table>
    </seed-card-table-body>
  </seed-card-table>
}
